import { getColorFromToken, IChartDataPoint, IVerticalStackedChartProps, IVSChartDataPoint, StackedBarChart, VerticalStackedBarChart } from "@fluentui/react-charting";
import { FunctionComponent } from "react";
import { getColorTable } from "../util";
import { SupplyStats, SupplyStatsSummary } from "../types";
import { appTheme } from "../theme";

export const ChartGeneralProgress: FunctionComponent<{ progress: number, readyOnFC: number, onShips?: number, maxNeed: number, minimal?: boolean, width?: number, height?: number, noProgress?: boolean }> = (props) => {

  const points: IChartDataPoint[] = [
    {
      legend: 'Delivered',
      data: props.progress,
      color: appTheme.palette.tealLight,
    },
    {
      legend: 'On ships',
      data: props.onShips,
      color: appTheme.palette.teal,
    },
    {
      legend: 'Ready on FCs',
      data: props.readyOnFC,
      color: appTheme.palette.tealDark,
    },
    {
      legend: 'Remaining',
      data: props.maxNeed - props.progress - props.readyOnFC - (props.onShips ?? 0),
      placeHolder: true,
      color: 'grey',
    },
  ];

  if (!props.readyOnFC) { points.splice(2, 1); }
  if (!props.onShips) { points.splice(1, 1); }
  if (props.noProgress) { points.splice(0, 1); }

  return <div className={props.width ? undefined : 'chart'} >
    <StackedBarChart
      ignoreFixStyle
      hideLegend={props.minimal}
      styles={{
        chart: { width: props.width, height: props.height, margin: 0, padding: 0 },
      }}
      data={{
        chartTitle: props.minimal ? undefined : `Total progress:`,
        chartData: points,
      }}
    />
  </div>;
};

export const ChartByCmdrs: FunctionComponent<{ summary: SupplyStatsSummary, cmdrColors: Record<string, string> }> = (props) => {
  const { summary, cmdrColors } = props;
  const points: IChartDataPoint[] = Object.keys(summary.cmdrs)
    .sort()
    .map(cmdr => ({
      legend: cmdr,
      data: summary.cmdrs[cmdr],
      color: getColorFromToken(cmdrColors[cmdr]),
    }));


  return <div className="chart" >
    <StackedBarChart
      ignoreFixStyle
      data={{
        chartTitle: 'Cargo delivered by Commander:',
        chartData: points,
      }}
      enabledLegendsWrapLines
    />
  </div>;
};

export const ChartByCmdrsOverTime: FunctionComponent<{ summary: SupplyStatsSummary, complete: boolean }> = (props) => {
  const { summary } = props;
  const colors = getColorTable(Object.keys(summary.cmdrs));
  const mins30 = 1000 * 60 * 30;

  // map raw data into chart dataand calculate the time span across the data points
  let minTime = Date.now();
  let maxTime = 0;
  const data: IVerticalStackedChartProps[] = summary.stats.map(ss => {
    // add 30 mins so each bar fills the hour rather than straddling it
    const d = new Date(new Date(ss.time).getTime() + mins30);
    if (d.getTime() < minTime) { minTime = d.getTime() }
    if (d.getTime() > maxTime) { maxTime = d.getTime() }
    return {
      xAxisPoint: d,
      chartData: mapDay(ss, colors),
    };
  });

  // add an extra empty data point at the beginning so we see the initial hour
  data.unshift({
    xAxisPoint: new Date(summary.stats[0].time),
    chartData: [],
  });

  // if not complete: add an extra empty entry so the chart extends to the current time
  if (!props.complete) {
    // the next hour mark
    maxTime = new Date(new Date().toISOString().substring(0, 14) + '00:00Z').getTime() + (1000 * 60 * 60);
    data.push({
      xAxisPoint: new Date(maxTime),
      chartData: [],
    });
  }

  // calculate how wide the bars can be to fill on hour's worth of pixels
  const width = 480;
  const timeDiffHours = 2 + ((maxTime - minTime) / 1000 / 60 / 60);
  let widthHour = ((width - 60) / timeDiffHours) - 1;
  // console.log(`${maxTime} // timeDiffHours: ${timeDiffHours} /widthHour: ${widthHour}`);
  if (widthHour < 1) widthHour = 1; // with a minimum of 1px

  return <div style={{ width: `${width}px` }}>
    <VerticalStackedBarChart
      chartTitle='Cargo deliveries by Commander over time'
      legendProps={{ allowFocusOnLegends: false }}
      isCalloutForStack
      data={data}
      width={width}
      height={200}
      styles={{ chartWrapper: 'cmdrs-over-time' }}
      barWidth={widthHour}
      maxBarWidth={widthHour}
      enableReflow
      hideTickOverlap
      wrapXAxisLables
      enabledLegendsWrapLines
    />
  </div>;
};

const mapDay = (ss: SupplyStats, colors: Record<string, string>): IVSChartDataPoint[] => {
  return Object.keys(ss.cmdrs)
    .sort()
    .map(cmdr => ({
      legend: cmdr,
      data: ss.cmdrs[cmdr],
      color: getColorFromToken(colors[cmdr]),
      xAxisCalloutData: new Date(ss.time).toLocaleString(),

    }) as IVSChartDataPoint);
};

